// ============================================
// NRS.03 — Conversation Engine Types
// ============================================
// Schema chain: TriageData + LabResultInjection → NurseResponse (phase-managed)
// Contract: 3-phase state machine (pre→mid→post), Fashion Plate persona
// ============================================

// ── Phase State Machine ──

export type NursePhase = 'pre-analysis' | 'mid-analysis' | 'post-triage';

export interface PhaseTransition {
  from: NursePhase;
  to: NursePhase;
  trigger: PhaseTransitionTrigger;
  timestamp: Date;
}

export type PhaseTransitionTrigger =
  | 'lab_results_injected'
  | 'triage_complete'
  | 'build_protocol_marker'
  | 'manual_override';

// ── Modal Markers ──

export const MODAL_MARKERS = [
  '[SHOW_GOALS_MODAL]',
  '[SHOW_MEDS_MODAL]',
  '[SHOW_ALLERGIES_MODAL]',
  '[SHOW_PREGNANCY_MODAL]',
  '[SHOW_REPORT]',
  '[BUILD_PROTOCOL]',
  '[SHOW_EMAIL_MODAL]',
] as const;

export type ModalMarker = typeof MODAL_MARKERS[number];

// ── Conversation Context ──

export interface ConversationContext {
  sessionId: string;
  patientId: string;
  phase: NursePhase;
  turnNumber: number;
  /** Collected patient data */
  collectedConcerns: string;
  collectedGoals: string[];
  collectedMeds: string[];
  collectedAllergies: string[];
  isPregnant: boolean;
  triageComplete: boolean;
  /** Whether lab results have been injected */
  labResultsInjected: boolean;
  /** NRS.01 D#4: Extraction failure reason (when extractionStatus === 'failed') */
  labFailureReason?: string;
  /** NRS.01 D#4: Whether injected results are partial */
  labResultsPartial: boolean;
  /** Token budget consumed so far */
  tokenBudgetUsed: number;
  /** Maximum token budget */
  tokenBudgetMax: number;
  /** Phase transition history */
  phaseTransitions: PhaseTransition[];
}

// ── Nurse Response ──

export interface NurseResponse {
  /** The response text (post-filter) */
  text: string;
  /** Modal marker extracted from response, if any */
  marker: ModalMarker | null;
  /** Current phase after this response */
  phase: NursePhase;
  /** Turn number for this response */
  turnNumber: number;
  /** Whether the response was regenerated due to filter violation */
  wasRegenerated: boolean;
  /** Number of regeneration attempts */
  regenerationAttempts: number;
  /** Whether the safe fallback was used */
  usedSafeFallback: boolean;
  /** Token count for this response */
  tokenCount: number;
}

// ── Token Budget ──

export interface TokenBudget {
  maxTokens: number;
  usedTokens: number;
  remainingTokens: number;
  warningThreshold: number;
}

// ── Nurse Completion Event (→ PRO.01) ──

export interface NurseCompletionEvent {
  sessionId: string;
  patientId: string;
  safetyDataProfileComplete: boolean;
  triggerSource: 'build_protocol_marker' | 'auto_complete';
  emittedAt: Date;
  turnNumber: number;
}

// ── Metrics ──

export interface ConversationEngineMetrics {
  totalCalls: number;
  successRate: number;
  avgLatencyMs: number;
  errorsByType: Record<string, number>;
  accuracyOnGoldStandard: number;
}

// ── Injectable Interfaces ──

export interface LLMProvider {
  chat(
    systemPrompt: string,
    history: Array<{ role: 'user' | 'assistant'; content: string }>,
    userMessage: string,
  ): Promise<{ text: string; tokenCount: number }>;
}
